import React, { useMemo } from 'react'; // ADDED: useMemo for performance optimization
import { Checkbox, FormControlLabel, Grid, Tooltip } from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import {
  ButtonsStyle,
  CenteredGrid,
  CheckboxStyle,
  FormControlLabelStyle,
  NotesTitle,
  NotesValues,
} from '../../pages/ThreadsDetails/Style/SingleThreat.style';
import Loader from '../Loader/Loader';
import GradientButton from '../Button/GradientButton';
import CustomToolTip from '../CustomToolTip/CustomToolTip';
import { areNotesEqual } from '../../utils/notesUtils'; // ADDED: Import utility function for note comparison
import { useNotesValuesQuery } from '../../queries/noteComments';

interface NoteListProps {
  handleNotesChange: (noteId: number) => void;
  selectedNoteIds: number[];
  automatedNoteIds?: number[];
  handleApplyNotes?: () => void;
  showApplyButton?: boolean;
  // Notes currently applied to the threat (state.noteComments.getNoteId
  // equivalent) - only meaningful when showApplyButton is used (NotesComments).
  originalNoteIds?: number[];
  isOriginalNoteIdsLoading?: boolean;
  // Replaces the former createNotes.loading (Apply button spinner/disable).
  isApplying?: boolean;
}

const NoteList = ({
  handleNotesChange,
  selectedNoteIds,
  automatedNoteIds,
  handleApplyNotes,
  showApplyButton,
  originalNoteIds,
  isOriginalNoteIdsLoading,
  isApplying,
}: NoteListProps) => {
  const { data: notesValues, isLoading: notesValuesLoading } =
    useNotesValuesQuery();

  const isLoading = !!isOriginalNoteIdsLoading || notesValuesLoading;

  /**
   * ADDED: Smart change detection to enable/disable Apply button
   *
   * Calculates if there are changes between selected notes and original saved notes.
   * Uses useMemo for performance optimization - only recalculates when dependencies change.
   *
   * Previous behavior: Button was disabled when selectedNoteIds.length === 0
   * New behavior: Button is disabled only when there are no changes
   *
   * This allows the Apply button to be enabled even when all notes are deselected,
   * fixing the bug where users couldn't delete the last remaining note.
   *
   * @returns {boolean} true if there are changes, false otherwise
   */
  const hasChanges = useMemo(() => {
    // Compare selected notes with original notes using utility function
    // Returns true if arrays are different (meaning there are changes)
    return !areNotesEqual(selectedNoteIds, originalNoteIds || []);
  }, [selectedNoteIds, originalNoteIds]); // Recalculate only when these dependencies change

  return (
    <>
      {isLoading ? (
        <CenteredGrid container>
          <Loader size={24} />
        </CenteredGrid>
      ) : (
        <>
          {notesValues?.map((item: any, index: number) => (
            <Grid item xs={6} key={index}>
              <NotesTitle>{item.name}</NotesTitle>
              {item.note_values?.map((note: any, noteIndex: number) => {
                if (note.deleted_at) return null;
                return (
                  <FormControlLabel
                    key={note.id}
                    sx={FormControlLabelStyle}
                    control={
                      <Checkbox
                        sx={CheckboxStyle}
                        size="small"
                        checked={selectedNoteIds?.includes(note.id)}
                        onChange={() => handleNotesChange(note.id)}
                      />
                    }
                    label={
                      <NotesValues data-testid={`note-${note.name}`}>
                        <CustomToolTip
                          title={note.description}
                          arrow
                          placement="top"
                        >
                          {note.name}
                        </CustomToolTip>
                        {automatedNoteIds?.includes(note.id) && (
                          <CustomToolTip
                            title="Note automatically generated by the system"
                            placement="top"
                          >
                            <AutoAwesome
                              data-testid={`automated-note-icon-${note.name}`}
                              sx={{
                                marginLeft: '5px',
                                cursor: 'pointer',
                                fontSize: 19,
                                color: 'secondary.main',
                              }}
                            />
                          </CustomToolTip>
                        )}
                      </NotesValues>
                    }
                  />
                );
              })}
            </Grid>
          ))}
          {showApplyButton && (
            <ButtonsStyle>
              {/* MODIFIED: Changed disabled condition to use hasChanges instead of length check */}
              <GradientButton
                variant="contained"
                disabled={
                  !hasChanges || // NEW: Disable only when there are no changes (not when empty)
                  isApplying // Keep: Disable during loading
                }
                onClick={handleApplyNotes}
              >
                {isApplying ? <Loader size={18} /> : 'Apply'}
              </GradientButton>
            </ButtonsStyle>
          )}
        </>
      )}
    </>
  );
};

export default NoteList;
